import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { 
  fetchAdsAsync,
  clearError, 
  setFilters, 
  clearFilters,
  setPage
} from '../store/slices/adsSlice';
import LoadingSpinner from './LoadingSpinner';
import { formatDateEuropean } from '../utils/dateUtils';
import './Students.css';
import './GroupForm.css';

const Ads: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  
  const { ads, loading, error, pagination, filters } = useAppSelector((state) => state.ads);

  const [searchInput, setSearchInput] = useState(filters.search);
  const initialLoadRef = useRef(false);

  // Load initial data
  useEffect(() => {
    if (!initialLoadRef.current) {
      initialLoadRef.current = true;
    }
  }, [dispatch]);

  // Load ads data when filters change
  const loadAds = useCallback(() => {
    dispatch(fetchAdsAsync({
      page: pagination.currentPage,
      limit: pagination.itemsPerPage,
      search: filters.search
    }));
  }, [dispatch, pagination.currentPage, pagination.itemsPerPage, filters]);

  useEffect(() => {
    loadAds();
  }, [loadAds]);

  const handleSearch = useCallback(() => {
    dispatch(setFilters({ 
      search: searchInput.trim()
    }));
  }, [dispatch, searchInput]);

  const handleClearFilters = useCallback(() => {
    setSearchInput('');
    dispatch(clearFilters());
  }, [dispatch]);

  const handlePageChange = useCallback((page: number) => {
    dispatch(setPage(page));
  }, [dispatch]);

  const formatDate = useCallback((dateString: string) => {
    return formatDateEuropean(dateString);
  }, []);

  const formatBudget = (budget: number | null | undefined) => {
    if (!budget) return '-';
    return new Intl.NumberFormat('ru-RU').format(budget);
  };

  const formatCPC = (budget: number | null | undefined, consultationCount: number | undefined) => {
    if (!budget || !consultationCount || consultationCount === 0) return '-';
    const cpc = budget / consultationCount;
    return new Intl.NumberFormat('ru-RU', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(cpc);
  };

  const formatCAC = (budget: number | null | undefined, paidCount: number | undefined) => {
    if (!budget || !paidCount || paidCount === 0) return '-';
    const cac = budget / paidCount;
    return new Intl.NumberFormat('ru-RU', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(cac);
  };

  const formatPlatform = (platform: string) => {
    return platform.charAt(0).toUpperCase() + platform.slice(1);
  };

  const formatChannelType = (channelType: string) => {
    return channelType.charAt(0).toUpperCase() + channelType.slice(1);
  };

  const formatGoal = (goal: string) => {
    const goalMap: Record<string, string> = {
      ig_followers: 'IG Followers',
      tg_followers: 'TG Followers',
      sales: 'Sales'
    };
    return goalMap[goal] || goal;
  };

  if (loading && ads.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className="students-container">
      <div className="page-header">
        <div className="page-header-content">
          <h1>Ads</h1>
          <p>Manage advertising campaigns</p>
        </div>
        <button 
          onClick={() => navigate('/ads/create')}
          className="btn-primary"
        >
          + Create Ad
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => dispatch(clearError())} className="close-error">×</button>
        </div>
      )}

      {/* Search and Filters */}
      <div className="filter-bar">
        <input
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search by name..."
          className="search-input"
          onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
        />

        <button onClick={handleSearch} className="btn-primary">
          Search
        </button>

        <button onClick={handleClearFilters} className="btn-secondary">
          Clear
        </button>
      </div>

      {/* Results Summary */}
      <div className="results-summary">
        <p>
          Showing {ads.length} of {pagination.totalItems} ads
          {filters.search && ` matching "${filters.search}"`}
        </p>
      </div>

      {/* Ads Table */}
      <div className="students-table-container vocabulary-table">
        {ads.length === 0 ? (
          <div className="no-students">
            <p>No ads found.</p>
            {filters.search && (
              <button onClick={handleClearFilters} className="btn-primary">
                Clear filters to see all ads
              </button>
            )}
          </div>
        ) : (
          <table className="students-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Platform</th>
                <th>Channel Type</th>
                <th>Goal</th>
                <th>Start Date</th>
                <th>End Date</th>
                <th>Budget</th>
                <th>Consultations</th>
                <th title="Cost Per Consultation">CPC</th>
                <th title="Customer Acquisition Cost">CAC</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {ads.map((ad) => (
                <tr key={ad.id}>
                  <td>
                    <strong>{ad.name}</strong>
                    {ad.description && (
                      <div style={{ fontSize: '0.9em', color: '#6b7280', marginTop: '4px' }}>
                        {ad.description}
                      </div>
                    )}
                  </td>
                  <td>
                    {formatPlatform(ad.platform)}
                  </td>
                  <td>
                    {formatChannelType(ad.channelType)}
                  </td>
                  <td>
                    {formatGoal(ad.goal)}
                  </td>
                  <td>
                    {formatDate(ad.dateStart)}
                  </td>
                  <td>
                    {formatDate(ad.dateEnd)}
                  </td>
                  <td>
                    {formatBudget(ad.budget)}
                  </td>
                  <td>
                    {ad.consultationCount || 0}
                  </td>
                  <td>
                    {formatCPC(ad.budget, ad.consultationCount)}
                  </td>
                  <td>
                    {formatCAC(ad.budget, ad.paidCount)}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        onClick={() => navigate(`/ads/edit/${ad.id}`)}
                        className="btn-icon"
                        data-tooltip="Edit ad"
                      >
                        ✎
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="pagination">
          <button 
            onClick={() => handlePageChange(pagination.currentPage - 1)}
            disabled={pagination.currentPage === 1 || loading}
            className="btn-secondary"
          >
            Previous
          </button>
          
          <div className="pagination-info">
            <span>
              Page {pagination.currentPage} of {pagination.totalPages}
            </span>
          </div>
          
          <button 
            onClick={() => handlePageChange(pagination.currentPage + 1)}
            disabled={pagination.currentPage === pagination.totalPages || loading}
            className="btn-secondary"
          >
            Next
          </button>
        </div>
      )}

      {loading && ads.length > 0 && (
        <div className="loading-overlay">
          <LoadingSpinner />
        </div>
      )}
    </div>
  );
};

export default Ads;
